import { Button, DialogContainer, Heading, Tabs, Text } from '@components'
import Cropper from 'cropperjs'
import 'cropperjs/dist/cropper.min.css'
import { RotateCcw } from 'lucide-solid'
import { For, Show, createSignal, onCleanup, onMount } from 'solid-js'
import { Box } from 'styled-system/jsx'
import { createWorker } from 'tesseract.js'
import { Confirm } from './Confirm'
import { CropZone } from './CropZone'
import { Scan } from './Scan'
import Trigger from './Trigger'
import { Upload } from './Upload'
import { extractHvfDetails } from './extractHvfDetails'
import type { ScanedDetails } from './scannedDetails'

const ImageCropAndOCR = (props: {
	onConfirmDetails: (details: ScanedDetails, callback: () => void) => void
}) => {
	const [isOpen, setIsOpen] = createSignal(false)
	const [imageSrc, setImageSrc] = createSignal<string | undefined>()
	const [cropper, setCropper] = createSignal<Cropper | null>(null)
	const [ocrResult, setOcrResult] = createSignal('')
	const [ocrError, setOcrError] = createSignal(false)
	const [croppedPreview, setCroppedPreview] = createSignal<string>()
	const [isScanning, setIsScanning] = createSignal(false)

	let imageRef: HTMLImageElement | undefined

	const isConfirmDisabled = () => !hvfDetails()?.exchange

	onMount(() => {
		reset()
	})

	onCleanup(() => {
		clearCropper()
	})

	// Clean up existing cropper instance
	const clearCropper = () => {
		if (cropper()) {
			cropper()?.destroy()
			setCropper(null)
		}
	}

	const hvfDetails: () => ScanedDetails | null = () => {
		if (ocrResult() === '' || ocrResult() === 'processing' || ocrResult() === 'Error') {
			return null
		}

		const result = extractHvfDetails(ocrResult())

		if (result === null) {
			setOcrError(true)
		}

		return result
	}

	const initializeCropper = () => {
		if (imageRef) {
			const newCropper = new Cropper(imageRef, {
				aspectRatio: 0, // Freeform cropping
				viewMode: 1,
				scalable: true,
				zoomable: true,
			})
			setCropper(newCropper)
		}
	}

	const handleCropAndOCR = async () => {
		setOcrError(false)
		setIsScanning(true)
		const currentCropper = cropper()
		if (currentCropper) {
			const croppedCanvas = currentCropper.getCroppedCanvas()
			setCroppedPreview(croppedCanvas.toDataURL())
			croppedCanvas.toBlob(async (blob) => {
				if (blob) {
					const worker = await createWorker('eng')
					setOcrResult('processing')
					try {
						const {
							data: { text },
						} = await worker.recognize(blob)
						setOcrResult(text)
					} catch (error) {
						console.error('Error during OCR:', error)
						setOcrResult('error')
					} finally {
						await worker.terminate()
						setIsScanning(false)
					}
				} else {
					setIsScanning(false)
				}
			})
		} else {
			setIsScanning(false)
		}
	}

	const onLoadImage = (imageUrl: string | undefined) => {
		setImageSrc(imageUrl)

		clearCropper()
	}

	const reset = () => {
		setImageSrc(undefined)
		setOcrResult('')
		cropper()?.destroy()
		setCropper(null)
		setOcrError(false)
		setCroppedPreview()
	}

	const options = [
		{ id: 'upload', label: 'Upload' },
		{ id: 'scan', label: 'Scan' },
		{ id: 'confirm', label: 'Confirm' },
	]

	const selectedTab = () => {
		if (hvfDetails() !== null) {
			return 'confirm'
		}

		if (imageSrc()) {
			return 'scan'
		}

		return 'upload'
	}

	return (
		<>
			<Trigger setIsOpen={setIsOpen} />
			<DialogContainer
				isOpen={isOpen()}
				setIsOpen={setIsOpen}
			>
				<Tabs.Root
					height="90vh"
					width="100%"
					overflow="auto"
					defaultValue="upload"
					value={selectedTab()}
					{...props}
				>
					<Tabs.List
						display="flex"
						alignItems="center"
						justifyContent="space-between"
					>
						<Box>
							<For each={options}>
								{(option) => (
									<Tabs.Trigger value={option.id}>
										<Heading size="sm">{option.label}</Heading>
									</Tabs.Trigger>
								)}
							</For>
							<Tabs.Indicator />
						</Box>

						<Button
							colorPalette="tscGreen"
							variant="subtle"
							onClick={reset}
							size="xs"
						>
							<RotateCcw />
							<Text
								display={{
									base: 'block',
									mdDown: 'none',
								}}
							>
								Start again
							</Text>
						</Button>
					</Tabs.List>
					<Tabs.Content value="upload">
						<Upload
							disabled={!!cropper()}
							onLoadImage={onLoadImage}
						/>
					</Tabs.Content>
					<Tabs.Content
						value="scan"
						display="grid"
						height="100%"
						gap={4}
						gridTemplateRows="1fr auto"
					>
						<CropZone showCropper={!!imageSrc()}>
							<img
								alt="hvf scan tool"
								ref={imageRef}
								src={imageSrc()}
								onLoad={initializeCropper}
								style={{ 'max-width': '650px' }}
							/>
						</CropZone>
						<Scan
							onReset={reset}
							onScan={handleCropAndOCR}
							isDisabled={!cropper() || isScanning()}
							isLoading={isScanning()}
						/>
					</Tabs.Content>
					<Tabs.Content value="confirm">
						<Show when={!isConfirmDisabled() && hvfDetails() !== null}>
							<Confirm
								preview={croppedPreview()}
								onConfirmDetails={(values) =>
									props.onConfirmDetails(values, () => setIsOpen(false))
								}
								isDisabled={!isConfirmDisabled()}
								details={hvfDetails()!}
							/>
						</Show>
					</Tabs.Content>
				</Tabs.Root>
			</DialogContainer>
		</>
	)
}

export default ImageCropAndOCR
